import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { ReactFlowProvider } from 'reactflow';
import {ServiceGraphResponse, ServiceMapInner} from "../../../pages/ServiceMap";

// Message interface for the ServiceGraphToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Service Graph response structure
interface ServiceGraph {
    nodes: Array<{
        id: string;
        type: string;
        position: { x: number; y: number };
        data: {
            label: string;
            [key: string]: any;
        };
    }>;
    edges: Array<{
        id: string;
        source: string;
        target: string;
        [key: string]: any;
    }>;
}

// Tool renderer for service graph visualization
export function ServiceGraphToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // If this is just the tool call and not the response, render the default tool call renderer
    if (!message.tool_call_response_content) {
        return <DefaultToolCallRenderer message={message} />;
    }

    let serviceGraph: ServiceGraphResponse | null = null;
    try {
        serviceGraph = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing service graph response data:', error);
        return null;
    }

    // Validate response
    if (!serviceGraph || !Array.isArray(serviceGraph.services) || !Array.isArray(serviceGraph.edges)) {
        return null;
    }

    return (
        <div className="w-full space-y-4">
            <MarkdownRenderer content={message.content} />

            <div className="rounded bg-backgroundmedium h-[500px]">
                <ReactFlowProvider>
                    <ServiceMapInner
                        className={"h-[500px] mt-0"}
                        serviceMap={serviceGraph}
                        showExpandButton={false}
                    />
                </ReactFlowProvider>
            </div>
        </div>
    );
}

// Static method to determine if this renderer should render a message
ServiceGraphToolRenderer.shouldRender = (message: Message): boolean => {
    return message.tool_name === "get_service_graph";
}; 